"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Analytics } from '@vercel/analytics/react';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { Checkbox } from './ui/Checkbox';
import { cn } from '@/lib/utils';

interface CookiePreferences {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  preferences: true,
  analytics: true,
  marketing: true,
};

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedConsent = localStorage.getItem('cookie_consent');
    if (!savedConsent) {
      setIsVisible(true);
    } else {
      try {
        setPreferences(JSON.parse(savedConsent));
      } catch (e) {
        // Invalid JSON, reset
        setIsVisible(true);
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));
    setPreferences(prefs);
    setIsVisible(false);
    setShowSettings(false);
    
    // Here you would typically trigger your analytics/marketing scripts to load
    // based on the new preferences
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleSaveSettings = () => {
    savePreferences(preferences);
  };

  if (!mounted) return null;

  return (
    <>
      {preferences.analytics && <Analytics />}
      
      {/* Main Consent Modal */}
      <Modal
        isOpen={isVisible && !showSettings}
        onClose={() => {}} // User must make a choice
        title="Cookies on WILLDONE"
        className="max-w-md"
        hideHeader={true}
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-bold leading-none tracking-tight text-primary font-display mb-4">
              Cookies on WILLDONE
            </h3>
            <div className="h-0.5 w-[20%] bg-teal rounded-full" />
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed">
            We use cookies to make the Website work properly, improve your experience, and for marketing. 
            You can manage your preferences or accept all cookies. For more details, read our{' '}
            <Link href="/cookie-policy" className="text-primary hover:underline font-medium">
              Cookie Policy
            </Link>.
          </p>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowSettings(true)}
              className="flex-1"
            >
              Customise
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAcceptAll}
              className="flex-1"
            >
              Accept All
            </Button>
          </div>
        </div>
      </Modal>

      {/* Settings Modal */}
      <Modal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title="Cookie Preferences"
        className="max-w-2xl"
      >
        <div className="space-y-6">
          <p className="text-muted-foreground text-sm">
            Manage your cookie preferences below. Strictly necessary cookies are always active as they are required for the website to function.
          </p>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="border border-foreground/10 rounded-lg p-4 bg-secondary/5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-bold text-foreground">Strictly Necessary</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Required for the website to function (security, session management, etc.). Always active.
                  </p>
                </div>
                <Checkbox checked disabled />
              </div>
            </div>

            <div className="border border-foreground/10 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-bold text-foreground">Preferences</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Remember your choices (e.g., language, display settings).
                  </p>
                </div>
                <Checkbox 
                  checked={preferences.preferences} 
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, preferences: checked }))} 
                />
              </div>
            </div>

            <div className="border border-foreground/10 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-bold text-foreground">Analytics</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Help us understand how visitors use the website so we can improve it.
                  </p>
                </div>
                <Checkbox 
                  checked={preferences.analytics} 
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, analytics: checked }))} 
                />
              </div>
            </div>

            <div className="border border-foreground/10 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-bold text-foreground">Marketing</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Track browsing activity to show relevant advertising or measure performance.
                  </p>
                </div>
                <Checkbox 
                  checked={preferences.marketing} 
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, marketing: checked }))} 
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-foreground/10">
            <Button 
              variant="outline" 
              onClick={() => setShowSettings(false)}
              className="sm:flex-1"
            >
              Back
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveSettings}
              className="sm:flex-1"
            >
              Save Preferences
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};


























